import { msg } from "../locales/translate";
import Signal, { DataProvider, Req } from "badmfck-signal";
import { GD } from "src/GD";

export class RateService {
	rates: DataProvider<{ projectID: number }, IAcquirer[]> = new DataProvider(
		{ cacheTime: 0 },
		"Rates",
	);

	constructor() {
		RateDispatcher.REQ_BY_TARGET.listener = async req =>
			this.getRatesByTarget(req);
		RateDispatcher.REQ_SAVE.listener = async req => {
			const { data, error } = await this.updateRate(req);
			if (error) return { error };
			if (!data) return { error: { code: -1, message: msg("noData") } };
			RateDispatcher.S_CHANGED.invoke({
				targetID: data.target_id,
				targetType: data.target_type,
			});
			return { data };
		};
		RateDispatcher.REQ_DEFAULT.listener = async req =>
			this.getDefaultRate(req);
		RateDispatcher.REQ_ADD.listener = async req => {
			const { data: addedRate, error } = await this.addRate(req);
			if (error) return { error };
			if (!addedRate) return { error: { code: -1, message: msg("noData") } };
			RateDispatcher.S_CHANGED.invoke({
				targetType: addedRate.target_type,
				targetID: addedRate.target_id,
			});
			return { data: addedRate };
		};
		RateDispatcher.REQ_DELETE.listener = async req => {
			const resp = await this.deleteRate(req);
			if (resp.error) return { error: resp.error };
			if (!resp.data)
				return { error: { code: -1, message: msg("couldNotDelete") } };

			RateDispatcher.S_CHANGED.invoke({
				targetID: req.target_id,
				targetType: req.target_type,
			});
			return { data: resp.data };
		};
	}

	async getDefaultRate(
		activeChannelID: number | null,
	): Promise<IAPIResponse<IRate> | null> {
		return GD.REQ_API.request({
			method: "rate/get",
			httpMethod: "POST",
			data: {
				id: 0,
				active_channel_id: activeChannelID ?? undefined,
			},
		});
	}

	async addRate(data: IRate): Promise<IAPIResponse<IRate>> {
		if (data.id !== 0) {
			return { error: { code: -1, message: msg("rateAlreadyExists") } };
		}
		if (!data.target_id) {
			return { error: { code: -1, message: msg("invalidTargetId") } };
		}
		if (!data.target_type) {
			return { error: { code: -1, message: msg("targetTypeIsMissing") } };
		}

		return GD.REQ_API.request({ method: "rate/add", data: data });
	}

	async getRatesByTarget(req: {
		targetType: TRateType;
		targetID: IRate["target_id"];
	}): Promise<IAPIResponse<IRate[]> | null> {
		if (!req) return null;
		const { targetID, targetType } = req;

		if (!targetType) {
			return { error: { code: -1, message: msg("invalidTargetType") } };
		}
		if (!targetID) {
			return {
				error: { code: -1, message: msg("templateInvalidId_256ea2c1", { v0: targetType }) },
			};
		}

		return GD.REQ_API.request({
			method: "rate/list/target",
			data: {
				target_type: targetType,
				target_id: targetID,
			},
		});
	}

	async updateRate(req: Partial<IRate>): Promise<IAPIResponse<IRate>> {
		if (!req) return { error: { code: -1, message: msg("noDataToUpdate") } };
		if (!req.id) return { error: { code: -1, message: msg("invalidId") } };

		return GD.REQ_API.request({
			method: "rate/update",
			data: req,
		});
	}

	async deleteRate(rate: IRate): Promise<IAPIResponse<boolean>> {
		if (!rate || !rate.id)
			return { error: { code: -1, message: msg("invalidId") } };

		return GD.REQ_API.request({
			method: `rate/delete/${rate.id}`,
			httpMethod: "DELETE",
			data: undefined,
		});
	}
}

export class RateDispatcher {
	static REQ_BY_TARGET = new Req<
		{ targetType: TRateType; targetID: IRate["target_id"] },
		IAPIResponse<IRate[]> | null
	>(undefined, "REQ_BY_TARGET");
	static REQ_SAVE = new Req<Partial<IRate>, IAPIResponse<IRate> | null>(
		undefined,
		"REQ_SAVE",
	);
	static REQ_DEFAULT = new Req<number | null, IAPIResponse<IRate> | null>(
		undefined,
		"REQ_DEFAULT",
	);
	static REQ_ADD = new Req<IRate, IAPIResponse<IRate>>(undefined, "REQ_ADD");
	static REQ_DELETE = new Req<IRate, IAPIResponse<boolean>>(
		undefined,
		"REQ_DELETE",
	);
	static S_CHANGED = new Signal<{
		targetType: TRateType;
		targetID: IRate["target_id"];
	}>("S_CHANGED");
}
